import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { Empresa as EmpresaType } from '../types';
import { logger } from '../utils/logger';

export interface Empresa {
  id: string;
  nombre: string;
  descripcion?: string;
  direccion?: string;
  ciudad?: string;
  pais?: string;
  telefono?: string;
  email?: string;
  slug?: string;
  logo?: string;
  ownerId: string;
  activa: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserEmpresa {
  id: string;
  user_id: string;
  empresa_id: string;
  role: 'OWNER' | 'ADMIN' | 'GERENTE' | 'CONTADOR' | 'EMPLEADO' | 'DOMICILIARIO'
    | 'owner' | 'admin' | 'vendedor' | 'empleado' | 'solo_lectura';
  empresa?: Empresa;
}

interface CompanyStore {
  // State
  empresas: Empresa[];
  userEmpresas: UserEmpresa[];
  currentEmpresa: Empresa | null;
  loading: boolean;
  error: string | null;

  // Actions
  fetchUserEmpresas: (userId: string) => Promise<void>;
  createEmpresa: (empresaData: Partial<Empresa>, userId: string) => Promise<Empresa>;
  updateEmpresa: (id: string, empresaData: Partial<Empresa>) => Promise<void>;
  deleteEmpresa: (id: string) => Promise<void>;
  setCurrentEmpresa: (empresa: Empresa | null) => void;
  switchEmpresa: (empresaId: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export const useCompanyStore = create<CompanyStore>((set, get) => ({
  // Initial state
  empresas: [],
  userEmpresas: [],
  currentEmpresa: null,
  loading: false,
  error: null,

  // Fetch all companies for a user - USE DATA FROM AUTH STORE TO AVOID DUPLICATE FETCHES
  fetchUserEmpresas: async (_userId: string) => {
    set({ loading: true, error: null });
    
    try {
      console.log('🏢 CompanyStore: Checking if data already exists in AuthStore...');
      
      // Import auth store to check if data already exists
      const { useAuthStore } = await import('./useAuthStore');
      const authStore = useAuthStore.getState();
      
      // If auth store already has companies, use that data instead of fetching again
      if (authStore.userEmpresas && authStore.userEmpresas.length > 0) {
        console.log('✅ CompanyStore: Using existing data from AuthStore:', authStore.userEmpresas.length);

        const empresasFrontend = authStore.userEmpresas
          .filter(ue => ue.empresa) // Filter out entries without empresa
          .map(ue => {
            const empresa = ue.empresa!; // Safe to assert after filter
            return {
              id: empresa.id,
              nombre: empresa.nombre,
              descripcion: empresa.descripcion || undefined,
              direccion: empresa.direccion || '',
              ciudad: empresa.ciudad || undefined,
              pais: empresa.pais || undefined,
              telefono: empresa.telefono || '',
              email: empresa.email,
              slug: empresa.slug || '',
              logo: empresa.logo || undefined,
              ownerId: empresa.owner_id || '',
              activa: empresa.activa !== undefined ? empresa.activa : true,
              createdAt: empresa.created_at || '',
              updatedAt: empresa.updated_at || ''
            };
          });
        
        // Use currentEmpresa from auth store if available
        const currentEmpresa = authStore.currentEmpresa ? {
          id: authStore.currentEmpresa.id,
          nombre: authStore.currentEmpresa.nombre,
          descripcion: authStore.currentEmpresa.descripcion || undefined,
          direccion: authStore.currentEmpresa.direccion || '',
          ciudad: authStore.currentEmpresa.ciudad || undefined,
          pais: authStore.currentEmpresa.pais || undefined,
          telefono: authStore.currentEmpresa.telefono || '',
          email: authStore.currentEmpresa.email,
          slug: authStore.currentEmpresa.slug || '',
          logo: authStore.currentEmpresa.logo || undefined,
          ownerId: authStore.currentEmpresa.owner_id || '',
          activa: authStore.currentEmpresa.activa !== undefined ? authStore.currentEmpresa.activa : true,
          createdAt: authStore.currentEmpresa.created_at || '',
          updatedAt: authStore.currentEmpresa.updated_at || ''
        } : null;
        
        set({ 
          empresas: empresasFrontend,
          currentEmpresa,
          loading: false,
          error: null
        });
        
        console.log('✅ CompanyStore: Data synced from AuthStore');
        return;
      }
      
      // Fallback: fetch directly if auth store doesn't have data yet
      console.log('🏢 CompanyStore: AuthStore has no data, fetching directly...');
      
      // IMPORTANT: userId here is from auth.users, but empresas.owner_id references usuarios.id
      // So we need to find the usuario record first, then search for empresas using usuarios.id
      // (authStore is already defined above, reusing it)
      const authUser = authStore.usuario;
      
      if (!authUser || !authUser.email) {
        throw new Error('Usuario no autenticado o sin email');
      }

      // Step 1: Find usuario record in usuarios table by email
      const { data: usuarioRecord, error: usuarioError } = await supabase
        .from('usuarios')
        .select('id')
        .eq('email', authUser.email)
        .maybeSingle();

      if (usuarioError) {
        console.error('❌ Error fetching usuario record:', usuarioError);
        throw new Error(`Error fetching usuario: ${usuarioError.message}`);
      }

      if (!usuarioRecord) {
        console.warn('⚠️ Usuario not found in usuarios table, no empresas to fetch');
        set({ 
          empresas: [],
          userEmpresas: [],
          loading: false 
        });
        return;
      }

      // Step 2: Search for empresas where user is owner OR member
      const usuarioId = usuarioRecord.id;
      console.log('🔍 Searching empresas using usuario.id:', usuarioId);
      
      // Get empresas where user is owner
      const { data: empresasOwner, error: ownerError } = await supabase
        .from('empresas')
        .select('id, nombre, descripcion, direccion, ciudad, pais, telefono, email, slug, owner_id, activa, configuracion, created_at, updated_at, emprendedores_de_fe_activo')
        .eq('owner_id', usuarioId);

      if (ownerError) {
        console.error('❌ Error fetching empresas (owner):', ownerError);
      }

      // Get empresas where user is a member via usuarios_empresa
      const { data: userEmpresasData, error: userEmpresasError } = await supabase
        .from('usuarios_empresa')
        .select(`
          empresa_id,
          empresas!inner (
            id,
            nombre,
            descripcion,
            direccion,
            ciudad,
            pais,
            telefono,
            email,
            slug,
            owner_id,
            activa,
            configuracion,
            created_at,
            updated_at
          )
        `)
        .eq('usuario_id', usuarioId);

      if (userEmpresasError) {
        console.warn('⚠️ Error fetching empresas (member) - this is OK if user is only owner:', userEmpresasError);
      }

      // Combine both lists (owner and member empresas)
      const empresasOwnerList = empresasOwner || [];
      const empresasMemberList = (userEmpresasData || [])
        .map((ue: any) => {
          // Handle both structures: ue.empresas (object) or ue.empresas (array)
          const empresa = Array.isArray(ue.empresas) ? ue.empresas[0] : ue.empresas;
          return empresa;
        })
        .filter((emp: any) => emp && typeof emp === 'object' && emp.id);
      
      // Merge and deduplicate by empresa.id
      const empresasMap = new Map<string, any>();
      empresasOwnerList.forEach(emp => empresasMap.set(emp.id, emp));
      empresasMemberList.forEach(emp => {
        if (emp && !empresasMap.has(emp.id)) {
          empresasMap.set(emp.id, emp);
        }
      });

      const empresas = Array.from(empresasMap.values());
      console.log('✅ Found empresas (owner + member):', empresas.length);

      // Convert to frontend format
      const empresasFrontend = empresas.map(empresa => ({
        id: empresa.id,
        nombre: empresa.nombre,
        descripcion: empresa.descripcion,
        direccion: empresa.direccion,
        ciudad: empresa.ciudad,
        pais: empresa.pais,
        telefono: empresa.telefono,
        email: empresa.email,
        slug: empresa.slug,
        logo: empresa.logo || empresa.configuracion?.branding?.logo || undefined,
        ownerId: empresa.owner_id,
        activa: empresa.activa !== undefined ? empresa.activa : true,
        createdAt: empresa.created_at,
        updatedAt: empresa.updated_at
      }));

      // SECURITY: Only auto-set first company if user actually has empresas
      // DO NOT set currentEmpresa if empresasFrontend is empty
      if (empresasFrontend.length > 0) {
        const { currentEmpresa } = get();
        if (!currentEmpresa) {
          const primeraEmpresa = empresasFrontend[0];
          if (primeraEmpresa) {
            set({ currentEmpresa: primeraEmpresa });
            console.log('🔧 AUTO-SET: Set first empresa as currentEmpresa:', primeraEmpresa.nombre);
          }
        }
      } else {
        // CRITICAL: If user has no empresas, clear currentEmpresa
        // This prevents new users from seeing other users' data
        set({ currentEmpresa: null });
        console.warn('⚠️ User has no empresas - currentEmpresa cleared. User must create a company.');
      }

      console.log('✅ Fetched user empresas:', empresasFrontend.length);

      set({ 
        userEmpresas: [], // Not using this complex structure anymore
        empresas: empresasFrontend,
        loading: false 
      });

    } catch (error) {
      console.error('❌ Error fetching user empresas:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        loading: false 
      });
    }
  },

  // Create new company
  createEmpresa: async (empresaData: Partial<Empresa>, _userId: string) => {
    set({ loading: true, error: null });
    
    try {
      console.log('🏢 Creating new empresa:', empresaData);

      // Step 1: Get authenticated user - prefer auth store usuario, else Supabase auth (for new signups)
      const { useAuthStore } = await import('./useAuthStore');
      const authStore = useAuthStore.getState();
      let authUser = authStore.usuario;
      let authId: string | null = null;
      if (!authUser?.id) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          authId = user.id;
          authUser = {
            id: user.id,
            email: user.email ?? '',
            nombre: user.user_metadata?.nombre ?? user.email?.split('@')[0] ?? 'Usuario',
            apellido: '',
            telefono: '',
          } as any;
        }
      } else {
        authId = (authUser as any).auth_id ?? (await supabase.auth.getUser()).data.user?.id ?? null;
      }
      
      if (!authUser || !authUser.email) {
        throw new Error('Usuario no autenticado o sin email');
      }

      // Step 2: Check if usuario exists in usuarios table, create if not
      let usuarioId: string;
      const { data: existingUsuario, error: usuarioFetchError } = await supabase
        .from('usuarios')
        .select('id')
        .eq('email', authUser.email)
        .maybeSingle();

      if (usuarioFetchError && usuarioFetchError.code !== 'PGRST116') {
        throw new Error(`Error verificando usuario: ${usuarioFetchError.message}`);
      }

      if (!existingUsuario) {
        // Create usuario record if it doesn't exist (include auth_id for new signups)
        console.log('👤 Usuario no encontrado en tabla usuarios, creando registro...');
        const { data: newUsuario, error: usuarioCreateError } = await supabase
          .from('usuarios')
          .insert({
            auth_id: authId ?? undefined,
            email: authUser.email,
            nombre: authUser.nombre || authUser.email.split('@')[0],
            apellido: (authUser as any).apellido ?? '',
            telefono: (authUser as any).telefono ?? '',
            tipo_usuario: 'ADMIN',
            rol: 'OWNER',
            activo: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select('id')
          .single();

        if (usuarioCreateError || !newUsuario) {
          throw new Error(`Error creando usuario en tabla usuarios: ${usuarioCreateError?.message || 'Unknown error'}`);
        }

        usuarioId = newUsuario.id;
        console.log('✅ Usuario creado en tabla usuarios:', usuarioId);
      } else {
        usuarioId = existingUsuario.id;
        console.log('✅ Usuario encontrado en tabla usuarios:', usuarioId);
      }

      // Step 3: Create empresa with usuario_id from usuarios table (not auth.users ID)
      const { TERMINOS_VERSION_ACTUAL } = await import('../constants/terminos');
      const { getCiudadDefaultPorPais } = await import('../utils/constants');
      
      // Use selected country or default to CO; set default city based on country
      const paisCode = (empresaData as any).pais || 'CO';
      const ciudad = (empresaData as any).ciudad || getCiudadDefaultPorPais(paisCode);
      
      const { data: empresaCreated, error: empresaError } = await supabase
        .from('empresas')
        .insert({
          nombre: empresaData.nombre,
          descripcion: empresaData.descripcion || null,
          pais: paisCode,
          ciudad: ciudad || null,
          direccion: empresaData.direccion || null,
          telefono: empresaData.telefono || null,
          email: empresaData.email || null,
          slug: null, // Slug will be set in configuration
          owner_id: usuarioId, // Use usuarios.id, not auth.users.id
          terminos_version: TERMINOS_VERSION_ACTUAL,
          terminos_aceptados_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (empresaError) {
        throw new Error(`Error creating empresa: ${empresaError.message}`);
      }

      // Step 4: Create user-empresa relationship (trigger should handle this, but we'll try manually too)
      // The trigger auto_assign_empresa_owner_trigger should create this automatically
      // But we'll also try to create it manually in case the trigger fails
      console.log('✅ Empresa created with owner_id:', empresaCreated.owner_id);
      console.log('👤 Creating usuarios_empresa relationship...');
      
      // Wait a moment for the trigger to complete
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Check if relationship already exists (created by trigger)
      const { data: existingRelationship } = await supabase
        .from('usuarios_empresa')
        .select('id')
        .eq('usuario_id', usuarioId)
        .eq('empresa_id', empresaCreated.id)
        .maybeSingle();
      
      if (existingRelationship) {
        console.log('✅ Usuarios_empresa relationship already exists (created by trigger)');
      } else {
        // Try to create it manually (trigger may have failed)
        const { error: userEmpresaError } = await supabase
          .from('usuarios_empresa')
          .insert({
            usuario_id: usuarioId,
            empresa_id: empresaCreated.id,
            role: 'OWNER'
          });

        if (userEmpresaError) {
          console.warn('⚠️ Error creating usuarios_empresa relationship:', userEmpresaError);
          console.log('ℹ️ Empresa was created successfully, but user-empresa relationship failed');
          console.log('ℹ️ This may be due to RLS policy - check if trigger created it instead');
          // Don't fail the empresa creation if relationship creation fails
          // The empresa is still accessible via owner_id
        } else {
          console.log('✅ Usuarios_empresa relationship created successfully (manual)');
        }
      }

      logger.info('✅ Empresa created successfully:', empresaCreated.id);

      // Update current usuario with empresa_id and rol_id (Admin) so RBAC and permissions work
      try {
        const { useAuthStore } = await import('./useAuthStore');
        await useAuthStore.getState().createUserAfterEmpresaSetup(empresaCreated.id);
      } catch (linkErr) {
        logger.warn('⚠️ Could not link usuario to empresa (createUserAfterEmpresaSetup):', linkErr);
      }

      // Step 5: Create default pipeline stages for the new empresa
      try {
        logger.info('🔄 Creating default pipeline stages for new empresa...');
        const { usePipelineStore } = await import('./usePipelineStore');
        await usePipelineStore.getState().createDefaultStages(empresaCreated.id);
        logger.info('✅ Default pipeline stages created');
      } catch (stageError) {
        logger.warn('⚠️ Failed to create default pipeline stages (trigger should handle this):', stageError);
        // Don't fail empresa creation if stages creation fails - trigger will handle it
      }

      // Step 6: Create default WhatsApp message templates for the new empresa
      try {
        logger.info('🔄 Creating default WhatsApp message templates for new empresa...');
        const { supabase } = await import('../lib/supabase');
        const { error: templateError } = await supabase.rpc('create_default_message_templates_for_empresa', {
          empresa_id_param: empresaCreated.id
        });

        if (templateError) {
          logger.warn('⚠️ RPC call failed, trying manual creation:', templateError);
          // Fallback: Try to create templates manually via INSERT
          const { useMessageTemplatesStore } = await import('./useMessageTemplatesStore');
          await useMessageTemplatesStore.getState().fetchTemplates(empresaCreated.id);
          logger.info('✅ Default message templates created (via fetchTemplates fallback)');
        } else {
          logger.info('✅ Default message templates created');
        }
      } catch (templateError) {
        logger.warn('⚠️ Failed to create default message templates (trigger should handle this):', templateError);
        // Don't fail empresa creation if templates creation fails - trigger will handle it
      }

      // Convert to frontend format
      const newEmpresa = {
        id: empresaCreated.id,
        nombre: empresaCreated.nombre,
        descripcion: empresaCreated.descripcion || undefined,
        direccion: empresaCreated.direccion || '',
        ciudad: empresaCreated.ciudad || undefined,
        pais: empresaCreated.pais || undefined,
        telefono: empresaCreated.telefono || '',
        email: empresaCreated.email || '',
        slug: empresaCreated.slug || '',
        logo: empresaCreated.logo || undefined,
        ownerId: empresaCreated.owner_id || '',
        activa: empresaCreated.activa !== undefined ? empresaCreated.activa : true,
        createdAt: empresaCreated.created_at || '',
        updatedAt: empresaCreated.updated_at || ''
      } as Empresa;

      // Update local state
      set(state => ({
        empresas: [...state.empresas, newEmpresa],
        currentEmpresa: newEmpresa,
        loading: false
      }));

      // Also update AuthStore with the new empresa
      try {
        // Use the AuthStore's update methods to properly update state
        const authStoreInstance = useAuthStore.getState();
        const updatedUserEmpresas = [
          ...(authStoreInstance.userEmpresas || []),
          {
            id: `temp-${empresaCreated.id}`,
            user_id: authUser.id,
            empresa_id: empresaCreated.id,
            role: 'OWNER' as const,
            created_at: new Date().toISOString(),
            empresa: empresaCreated
          }
        ];
        
        // Convert empresaCreated to frontend format for AuthStore
        const empresaForAuthStore = {
          id: empresaCreated.id,
          nombre: empresaCreated.nombre,
          descripcion: empresaCreated.descripcion || undefined,
          direccion: empresaCreated.direccion || '',
          ciudad: empresaCreated.ciudad || undefined,
          pais: empresaCreated.pais || undefined,
          telefono: empresaCreated.telefono || '',
          email: empresaCreated.email || '',
          slug: empresaCreated.slug || '',
          logo: empresaCreated.logo || undefined,
          ownerId: empresaCreated.owner_id || '',
          activa: empresaCreated.activa !== undefined ? empresaCreated.activa : true,
          createdAt: empresaCreated.created_at || '',
          updatedAt: empresaCreated.updated_at || ''
        };
        
        // Update AuthStore using Zustand's setState function
        useAuthStore.setState({ 
          userEmpresas: updatedUserEmpresas,
          currentEmpresa: empresaForAuthStore,
          usuario: {
            ...authStoreInstance.usuario!,
            currentEmpresa: empresaForAuthStore,
            empresaId: empresaCreated.id,
            empresa: empresaCreated.nombre
          }
        });
        
        console.log('✅ AuthStore updated with new empresa');
        
        // CRITICAL: Refresh user empresas to ensure everything is in sync
        // This will trigger sidebar refresh and permission checks
        await authStoreInstance.fetchUserEmpresas();
        console.log('✅ User empresas refreshed after company creation');
      } catch (authStoreError) {
        console.warn('⚠️ Error updating AuthStore:', authStoreError);
        // Don't fail the empresa creation if AuthStore update fails
      }

      return newEmpresa;

    } catch (error) {
      console.error('❌ Error creating empresa:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        loading: false 
      });
      throw error;
    }
  },

  // Update company
  updateEmpresa: async (id: string, empresaData: Partial<Empresa>) => {
    set({ loading: true, error: null });
    
    try {
      console.log('🏢 Updating empresa:', id, empresaData);

      const { error } = await supabase
        .from('empresas')
        .update({
          ...empresaData,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) {
        throw new Error(`Error updating empresa: ${error.message}`);
      }

      // Update local state
      set(state => ({
        empresas: state.empresas.map(empresa => 
          empresa.id === id 
            ? { ...empresa, ...empresaData, updated_at: new Date().toISOString() }
            : empresa
        ),
        currentEmpresa: state.currentEmpresa?.id === id 
          ? { ...state.currentEmpresa, ...empresaData, updated_at: new Date().toISOString() }
          : state.currentEmpresa,
        loading: false
      }));

      // Sync AuthStore's currentEmpresa (fuente de dirección para Vitrina/Tienda Pública).
      try {
        const { useAuthStore } = await import('./useAuthStore');
        const authEmpresa = useAuthStore.getState().currentEmpresa;
        if (authEmpresa && authEmpresa.id === id) {
          useAuthStore.getState().setCurrentEmpresa({ ...authEmpresa, ...empresaData } as typeof authEmpresa);
        }
      } catch (syncError) {
        console.warn('⚠️ Error syncing empresa update with AuthStore:', syncError);
      }

      console.log('✅ Empresa updated successfully');

    } catch (error) {
      console.error('❌ Error updating empresa:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        loading: false 
      });
      throw error;
    }
  },

  // Delete company
  deleteEmpresa: async (id: string) => {
    set({ loading: true, error: null });
    
    try {
      console.log('🗑️ Deleting empresa:', id);

      // Delete user-empresa relationships first
      const { error: userEmpresaError } = await supabase
        .from('usuarios_empresa')
        .delete()
        .eq('empresa_id', id);

      if (userEmpresaError) {
        throw new Error(`Error deleting user-empresa relationships: ${userEmpresaError.message}`);
      }

      // Delete empresa
      const { error: empresaError } = await supabase
        .from('empresas')
        .delete()
        .eq('id', id);

      if (empresaError) {
        throw new Error(`Error deleting empresa: ${empresaError.message}`);
      }

      // Update local state
      const remainingEmpresas = get().empresas.filter(empresa => empresa.id !== id);
      const newCurrentEmpresa = get().currentEmpresa?.id === id 
        ? (remainingEmpresas.length > 0 ? remainingEmpresas[0] : null)
        : get().currentEmpresa;

      set({
        empresas: remainingEmpresas,
        currentEmpresa: newCurrentEmpresa,
        loading: false
      });

      console.log('✅ Empresa deleted successfully');

    } catch (error) {
      console.error('❌ Error deleting empresa:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        loading: false 
      });
      throw error;
    }
  },

  // Set current empresa
  setCurrentEmpresa: (empresa: Empresa | null) => {
    set({ currentEmpresa: empresa });
    
    // NO localStorage - database is the source of truth
    // The empresa_id is stored in the usuarios table
    console.log('✅ CurrentEmpresa set in Zustand:', empresa?.nombre || 'null');
  },

  // Switch to different empresa
  switchEmpresa: async (empresaId: string) => {
    set({ loading: true, error: null });
    
    try {
      console.log('🔄 Cambiando de empresa a:', empresaId);
      
      const empresa = get().empresas.find(e => e.id === empresaId);
      if (!empresa) {
        throw new Error('Empresa no encontrada');
      }

      // Convert empresa to format expected by AuthStore
      // Note: CompanyStore uses string dates, but AuthStore expects Date objects
      const createdAt = empresa.createdAt ? new Date(empresa.createdAt) : new Date();
      const updatedAt = empresa.updatedAt ? new Date(empresa.updatedAt) : new Date();
      
      const empresaAuthFormat: EmpresaType = {
        id: empresa.id,
        nombre: empresa.nombre,
        descripcion: empresa.descripcion || undefined,
        direccion: empresa.direccion || undefined,
        ciudad: empresa.ciudad || undefined,
        pais: empresa.pais || undefined,
        telefono: empresa.telefono || undefined,
        email: empresa.email || '',
        slug: empresa.slug || undefined,
        logo: empresa.logo || undefined,
        owner_id: empresa.ownerId || undefined,
        created_at: createdAt.toISOString(),
        updated_at: updatedAt.toISOString(),
        activa: empresa.activa,
        createdAt,
        updatedAt
      };

      // Update both stores
      get().setCurrentEmpresa(empresa);
      set({ currentEmpresa: empresa, loading: false });

      // Also update AuthStore to keep both stores in sync
      try {
        const { useAuthStore } = await import('./useAuthStore');
        useAuthStore.getState().setCurrentEmpresa(empresaAuthFormat);
        console.log('✅ Sincronizado con AuthStore');
      } catch (syncError) {
        console.warn('⚠️ Error syncing with AuthStore:', syncError);
        // Don't fail if sync fails
      }

      // Save to localStorage for persistence
      localStorage.setItem('lastSelectedEmpresaId', empresaId);

      // Force a page reload to ensure all components refresh with new empresa data
      console.log('✅ Empresa cambiada a:', empresa.nombre);
      console.log('🔄 Recargando página para refrescar todos los datos...');
      window.location.reload();
      
    } catch (error) {
      console.error('❌ Error switching empresa:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        loading: false 
      });
      throw error;
    }
  },

  // Clear error
  clearError: () => set({ error: null }),

  reset: () => {
    set({
      empresas: [],
      userEmpresas: [],
      currentEmpresa: null,
      loading: false,
      error: null,
    });
  },
}));




